import { InputAdornment, TextField, TextFieldProps } from '@mui/material';
import { useLocaleState } from 'contexts/locale/locale-context';
import { Info } from 'lucide-react';
import React, { useMemo } from 'react';
import { Controller, UseControllerProps } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import Tooltip from './tooltip';
import { getComputedLabel } from 'utils/label-required-option';

type NumberFieldProps = TextFieldProps & {
  controller?: UseControllerProps<any>;
  dark?: boolean;
  error?: boolean;
  label: string;
  hiddenLabel?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  infoText?: string;
  type?: 'text' | 'tel' | 'password';
  disableDecimal?: boolean;
  decimalScale?: number;
  stopPropagation?: boolean;
};

const normalizeNumber = (formattedValue: string, locale: string): string => {
  let value = formattedValue;

  // Remove thousand separators
  switch (locale) {
    case 'en-gb':
    case 'es-cl':
      value = value.replace(/,/g, '');
      break;
    case 'nb':
      value = value.replace(/ /g, '');
      break;
    default:
      value = value.replace(/,/g, '');
  }

  // Replace decimal separator with dot
  switch (locale) {
    case 'nb':
      value = value.replace(/,/g, '.');
      break;
    // Add other locale-specific rules if needed
  }

  // Remove any remaining non-numeric characters except dot and minus
  return value.replace(/[^\d.-]/g, '');
};

const NumberField: React.FC<NumberFieldProps> = ({
  dark = false,
  error = false,
  hiddenLabel,
  disabled,
  decimalScale = 3,
  disableDecimal = false,
  helperText,
  startAdornment,
  endAdornment,
  label,
  infoText,
  onChange,
  slotProps,
  fullWidth,
  value,
  defaultValue,
  type = 'text',
  controller,
  ...props
}) => {
  const backgroundClass = dark ? 'Mui-backgroundDark' : 'Mui-backgroundLight';
  const { locale } = useLocaleState();

  const thousandSeparator = useMemo(() => {
    switch (locale) {
      case 'en-gb':
      case 'es-cl':
        return ',';
      case 'nb':
        return ' ';
      default:
        return ',';
    }
  }, [locale]);

  const decimalSeparator = useMemo(() => {
    switch (locale) {
      case 'en-gb':
      case 'es-cl':
        return '.';
      case 'nb':
        return ',';
      default:
        return '.';
    }
  }, [locale]);

  return controller ? (
    <Controller
      {...controller}
      render={({ field, fieldState }) => {
        const currentHelperText = fieldState.error?.message ?? helperText;
        const computedLabel = getComputedLabel(label, controller);

        return (
          <NumericFormat
            {...props}
            customInput={TextField}
            defaultValue={defaultValue as string}
            type={type}
            className={backgroundClass}
            value={field.value ?? ''}
            error={fieldState.invalid}
            onFocus={(event) => {
              event.target.select();
              props.onFocus && props.onFocus(event);
            }}
            onBlur={(data) => {
              field.onBlur();
              props.onBlur && props.onBlur(data);
            }}
            onChange={(e) => {
              const formattedValue = e.target.value;

              const rawValue = normalizeNumber(formattedValue, locale);

              field.onChange(rawValue); // raw numeric string

              onChange?.({
                target: {
                  name: props.name ?? '',
                  value: rawValue,
                },
              } as unknown as React.ChangeEvent<HTMLInputElement>);
            }}
            decimalSeparator={decimalSeparator}
            thousandSeparator={thousandSeparator}
            label={hiddenLabel ? '' : computedLabel}
            decimalScale={disableDecimal ? 0 : decimalScale}
            valueIsNumericString
            disabled={field.disabled ?? disabled}
            helperText={currentHelperText}
            fullWidth={fullWidth}
            slotProps={{
              ...slotProps,
              inputLabel: {
                className: backgroundClass,
                error: fieldState.invalid,
              },
              formHelperText: {
                className: backgroundClass,
                error: fieldState.invalid,
              },
              input: () => ({
                ref: props.inputRef,
                className: backgroundClass,
                error: fieldState.invalid,
                startAdornment: startAdornment && <InputAdornment position="start">{startAdornment}</InputAdornment>,
                endAdornment: (
                  <>
                    {endAdornment && <InputAdornment position="end">{endAdornment}</InputAdornment>}
                    {infoText && (
                      <InputAdornment position="end">
                        <Tooltip title={infoText}>
                          <Info />
                        </Tooltip>
                      </InputAdornment>
                    )}
                  </>
                ),
              }),
            }}
          />
        );
      }}
    />
  ) : (
    <NumericFormat
      {...props}
      customInput={TextField}
      defaultValue={defaultValue as string}
      type={type}
      className={backgroundClass}
      value={value as string}
      onChange={(e) => {
        const formattedValue = e.target.value;

        const rawValue = normalizeNumber(formattedValue, locale);

        onChange?.({
          target: {
            name: props.name ?? '',
            value: rawValue,
          },
        } as unknown as React.ChangeEvent<HTMLInputElement>);
      }}
      decimalSeparator={decimalSeparator}
      thousandSeparator={thousandSeparator}
      label={hiddenLabel ? '' : label}
      decimalScale={disableDecimal ? 0 : decimalScale}
      valueIsNumericString
      disabled={disabled}
      helperText={helperText}
      fullWidth={fullWidth}
      slotProps={{
        ...slotProps,
        inputLabel: {
          className: backgroundClass,
          error: error,
        },
        formHelperText: {
          className: backgroundClass,
          error: error,
        },
        input: () => ({
          ref: props.inputRef,
          className: backgroundClass,
          error: error,
          startAdornment: startAdornment && <InputAdornment position="start">{startAdornment}</InputAdornment>,
          endAdornment: (
            <>
              {endAdornment && <InputAdornment position="end">{endAdornment}</InputAdornment>}
              {infoText && (
                <InputAdornment position="end">
                  <Tooltip title={infoText}>
                    <Info />
                  </Tooltip>
                </InputAdornment>
              )}
            </>
          ),
        }),
      }}
    />
  );
};

export default React.memo(NumberField);
